import './App.css';
import { useState, useEffect } from 'react';
import { auth, db, googleProvider, facebookProvider } from './firebase';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, addDoc, updateDoc, doc, getDocs, deleteDoc, query, where, orderBy, serverTimestamp, setDoc, getDoc } from 'firebase/firestore';
import {
  Container, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, TextField,
  AppBar, Toolbar, Menu, MenuItem, Modal, Box, Checkbox, Switch, FormControlLabel, Avatar, Select, FormControl, List, ListItem, ListItemText, ListItemSecondaryAction, Divider, Chip, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, ThemeProvider
} from '@mui/material';
import { Add, Delete, AccountCircle, ArrowDropDown, Save, FolderOpen, MoreVert, GetApp, Publish, PictureAsPdf, Menu as MenuIcon, Email, Edit, BarChart } from '@mui/icons-material';
import GoogleLogo from './assets/google-logo.png';
import FacebookLogo from './assets/facebook-logo.png';
import SawLogo from './assets/saw-logo.png';
import CutCreatorLogoDark from './assets/cut-creator-logo-dark.png';
import UpgradeLogo from './assets/upgrade.png';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import CutLayoutVisualization from './CutLayoutVisualization';
import { handleExportPDF, handleExportSVG, handleExportDXF } from './utils/exportUtils';
import { handleUpgrade, fetchSubscriptionStatus, handleCancelSubscription } from './utils/subscriptionUtils';
import { loadUserProjects, handleSaveProject, handleLoadProject, handleDeleteProject, handleNewProject, loadCurrentProjectId, loadProjectById, saveCurrentProjectId, saveUserVizPreferences, loadUserVizPreferences } from './utils/projectUtils';
import { handleLogin, handleLogout, handleEmailLogin } from './utils/authUtils';
import { filterNumericInput, validateNumericField, validatePanel, validateStockSheet, validateKerfThickness, validateAllInputs } from './utils/validationUtils';
import { getMaterialTypes, getGrainIcon } from './utils/uiHelpers';
import { getUnitSymbol } from './utils/unitHelpers';
import { handlePanelChange as handlePanelChangeUtil, handlePanelBlur as handlePanelBlurUtil, togglePanelEnabled as togglePanelEnabledUtil, togglePanelGrain as togglePanelGrainUtil, addPanel as addPanelUtil, removePanel as removePanelUtil } from './utils/panelHandlers';
import { handleStockChange as handleStockChangeUtil, handleStockBlur as handleStockBlurUtil, toggleStockEnabled as toggleStockEnabledUtil, toggleStockGrain as toggleStockGrainUtil, addStock as addStockUtil, removeStock as removeStockUtil } from './utils/stockHandlers';
import { handleExportPanelsCsv as handleExportPanelsCsvUtil, handleImportPanelsCsv as handleImportPanelsCsvUtil, handleExportStockCsv as handleExportStockCsvUtil, handleImportStockCsv as handleImportStockCsvUtil } from './utils/csvUtils';
import { handleEnableAllPanels as handleEnableAllPanelsUtil, handleDisableAllPanels as handleDisableAllPanelsUtil, handleClearPanels as handleClearPanelsUtil, handleEnableAllStock as handleEnableAllStockUtil, handleDisableAllStock as handleDisableAllStockUtil, handleClearStock as handleClearStockUtil } from './utils/tableMenuHandlers';
import { handleRun as handleRunUtil } from './utils/optimizationUtils';
import createAppTheme from './theme';

// Format number with minimum decimal places (up to 3)
const formatDimension = (num) => {
  // Handle null/undefined values
  if (num == null || isNaN(num)) {
    return '0';
  }
  // Convert to 3 decimal places first
  const fixed3 = Number(num.toFixed(3));
  // Convert back to string
  let result = fixed3.toString();
  
  // Only remove trailing zeros after a decimal point
  // Example: 20.000 -> 20, 20.5 -> 20.5, 20.500 -> 20.5
  if (result.includes('.')) {
    result = result.replace(/\.?0+$/, '');
  }
  
  return result;
};
import { API_URL } from './config';
import { ReactComponent as DimensionsIcon } from './assets/dimensions-icon.svg';
import { ReactComponent as TagIcon } from './assets/tag-icon.svg';
import { ReactComponent as RotationIndicatorIcon } from './assets/rotation-indicator-icon.svg';
import { ReactComponent as ChecklistIcon } from './assets/checklist-icon.svg';
import { ReactComponent as RotateViewIcon } from './assets/rotate-view-icon.svg';

// Helper function to prevent text/icon selection on menu items
const preventSelection = (e) => {
  e.preventDefault();
  return false;
};

function App() {
  // Theme state management
  const [themeMode, setThemeMode] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('themeMode');
    if (saved) return saved;
    // Fall back to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const toggleTheme = () => {
    const newMode = themeMode === 'dark' ? 'light' : 'dark';
    setThemeMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  // Stripe Upgrade handler
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [savedProjects, setSavedProjects] = useState([]);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null); // Track user's subscription status
  const [manageSubscriptionModalOpen, setManageSubscriptionModalOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [priceInfo, setPriceInfo] = useState({ amount: 9.99, currency: 'USD', interval: 'month' }); // Default price info
  const [optimizationLoading, setOptimizationLoading] = useState(false); // Track optimization in progress

  // Fetch price information from Stripe
  useEffect(() => {
    const fetchPriceInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscription/price-info`);
        const data = await res.json();
        if (data.amount) {
          setPriceInfo(data);
        }
      } catch (error) {
        console.error('Error fetching price info:', error);
        // Keep default price if fetch fails
      }
    };
    fetchPriceInfo();
  }, []);

  // Handle Stripe redirect after payment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const sessionId = urlParams.get('session_id');

    if (status === 'success' && sessionId && user) {
      // Verify the checkout session
      const verifyCheckout = async () => {
        try {
          const token = await auth.currentUser.getIdToken();
          const res = await fetch(`${API_URL}/api/subscription/verify-checkout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ sessionId })
          });
          const data = await res.json();
          if (data.success) {
            alert('🎉 Welcome to Premium! Your subscription is now active.');
            // Refresh subscription status
            await fetchSubscriptionStatus(auth, setUserSubscription);
            // Clean up URL
            window.history.replaceState({}, document.title, '/');
          } else {
            alert('There was an issue verifying your payment. Please contact support.');
          }
        } catch (error) {
          console.error('❌ Error verifying checkout:', error);
          alert('Error verifying payment. Please contact support.');
        }
      };
      verifyCheckout();
    } else if (status === 'canceled') {
      alert('Payment canceled. You can upgrade anytime!');
      // Clean up URL
      window.history.replaceState({}, document.title, '/');
    }
  }, [user]);

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Ensure Firestore user document exists
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (!userSnap.exists()) {
            // Create user document if it doesn't exist
            await setDoc(userRef, {
              uid: currentUser.uid,
              email: currentUser.email,
              displayName: currentUser.displayName,
              createdAt: new Date().toISOString()
            });
            console.log('✅ Created Firestore user document for', currentUser.uid);
          }
        } catch (err) {
          console.error('❌ Error ensuring Firestore user doc:', err);
        }
        loadUserProjects(db, currentUser, setSavedProjects);
        
        // Load user visualization preferences
        try {
          const preferences = await loadUserVizPreferences(db, currentUser);
          if (preferences) {
            if (preferences.showDimensions !== undefined) setShowDimensions(preferences.showDimensions);
            if (preferences.showLabels !== undefined) setShowLabels(preferences.showLabels);
            if (preferences.vizRotation !== undefined) setVizRotation(preferences.vizRotation);
            if (preferences.showCutOrder !== undefined) setShowCutOrder(preferences.showCutOrder);
            if (preferences.showRotationIndicator !== undefined) setShowRotationIndicator(preferences.showRotationIndicator);
          }
        } catch (error) {
          console.error('❌ Error loading user viz preferences:', error);
        }
        
        // Load current project if one exists
        try {
          const currentProjectId = await loadCurrentProjectId(db, currentUser);
          if (currentProjectId) {
            const project = await loadProjectById(db, currentProjectId);
            if (project) {
        // Load the project data and ensure panels have ids arrays
        // Generate sequential IDs for all panel instances (1 to n)
        const loadedPanels = project.panels || [];
        let nextId = 1;
        const panelsWithIds = loadedPanels.map((p) => {
          // If panel already has ids array, use it; otherwise generate new ones
          if (p.ids && Array.isArray(p.ids) && p.ids.length > 0) {
            return p; // Already has ids, use as-is
          }
          
          // Generate new ids array based on quantity
          const quantity = parseInt(p.quantity) || 1;
          const ids = [];
          for (let i = 0; i < quantity; i++) {
            ids.push(nextId++);
          }
          return { ...p, ids };
        });
              setPanels(panelsWithIds);
              setStockSheets(project.stockSheets || []);
              const kerfValue = project.settings?.kerfThickness !== null && project.settings?.kerfThickness !== undefined ? project.settings.kerfThickness.toString() : '0.125';
              setKerfThickness(kerfValue);
              setLabelsOnPanels(project.settings?.labelsOnPanels ?? true);
              setDifferentMaterials(project.settings?.differentMaterials ?? true);
              setGrainDirection(project.settings?.grainDirection ?? true);
              setUnitSystem(project.settings?.unitSystem ?? 'imperial');
              // Load optimization results if they exist and have valid structure
              const loadedResults = project.lastOptimization || null;
              if (loadedResults && loadedResults.stock_sheets && Array.isArray(loadedResults.stock_sheets)) {
                setOptimizationResults(loadedResults);
              } else {
                setOptimizationResults(null);
              }
              setCurrentProjectId(project.id);
              setProjectName(project.projectName);
              setOriginalProjectName(project.projectName);
            } else {
              console.log('⚠️ Current project ID found but project no longer exists, clearing it');
              // Project was deleted, clear the current project ID
              await saveCurrentProjectId(db, currentUser, null);
            }
          }
        } catch (error) {
          console.error('❌ Error loading current project:', error);
        }
        
        // Fetch subscription status
        setTimeout(() => fetchSubscriptionStatus(auth, setUserSubscription), 500); // Small delay to ensure user state is set
      } else {
        setSavedProjects([]);
        setUserSubscription(null);
      }
    });
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Editable table state for panels - load from localStorage or use default
  const [panels, setPanels] = useState(() => {
    const saved = localStorage.getItem('cutlist_panels');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Return panels as-is (ids arrays are generated when needed)
        return parsed;
      } catch (e) {
        console.error('Error loading saved panels:', e);
      }
    }
    return [{ width: '', length: '', quantity: '', materialType: '', label: '', enabled: true, grainDirection: 'either' }];
  });
  
  // Editable table state for stock sheets - load from localStorage or use default
  const [stockSheets, setStockSheets] = useState(() => {
    const saved = localStorage.getItem('cutlist_stockSheets');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading saved stock sheets:', e);
      }
    }
    return [{ width: '', length: '', quantity: '', materialType: '', enabled: true, grainDirection: 'either' }];
  });

  // Save panels to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('cutlist_panels', JSON.stringify(panels));
  }, [panels]);

  // Save stock sheets to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('cutlist_stockSheets', JSON.stringify(stockSheets));
  }, [stockSheets]);

  // Settings state - must be declared before useEffect that uses them
  const [kerfThickness, setKerfThickness] = useState('0.125');
  const [labelsOnPanels, setLabelsOnPanels] = useState(true);
  const [differentMaterials, setDifferentMaterials] = useState(true);
  const [grainDirection, setGrainDirection] = useState(true);
  const [unitSystem, setUnitSystem] = useState('imperial'); // 'imperial', 'metric', 'meters', or 'millimeters'

  // Sidebar resize state
  const [sidebarWidth, setSidebarWidth] = useState(450);
  const [isResizing, setIsResizing] = useState(false);
  const [rightSidebarWidth, setRightSidebarWidth] = useState(300);
  const [isResizingRight, setIsResizingRight] = useState(false);
  
  // Hover state for cutting sequence tables (sheetIndex -> cutIndex)
  const [hoveredCuts, setHoveredCuts] = useState({});

  // Shared accordion styles - theme-aware
  const isDarkMode = themeMode === 'dark';
  const accordionStyles = {
    root: {
      bgcolor: isDarkMode ? '#161b22' : '#f6f8fa',
      boxShadow: 'none',
      borderBottom: isDarkMode ? '1px solid #30363d' : '1px solid #d0d7de',
      '&:before': { display: 'none' },
      '&.Mui-expanded': { margin: 0 }
    },
    summary: {
      px: 2,
      minHeight: '36px !important',
      height: 36,
      bgcolor: '#00d9ff',
      '&.Mui-expanded': { minHeight: '52px !important', height: 52 },
      '& .MuiAccordionSummary-content': {
        margin: 0,
        alignItems: 'center'
      },
      '&:hover': {
        bgcolor: '#00c4e6'
      }
    },
    summaryWithMenu: {
      px: 2,
      minHeight: '36px !important',
      height: 36,
      bgcolor: '#00d9ff',
      '&.Mui-expanded': { minHeight: '52px !important', height: 52 },
      '& .MuiAccordionSummary-content': {
        margin: 0,
        alignItems: 'center',
        justifyContent: 'space-between'
      },
      '&:hover': {
        bgcolor: '#00c4e6'
      }
    },
    summaryRed: {
      px: 2,
      minHeight: '36px !important',
      height: 36,
      bgcolor: '#d32f2f',
      '&.Mui-expanded': { minHeight: '52px !important', height: 52 },
      '& .MuiAccordionSummary-content': {
        margin: 0,
        alignItems: 'center'
      },
      '&:hover': {
        bgcolor: '#b71c1c'
      }
    },
    expandIcon: { color: '#0d1117', fontSize: '1.5rem' },
    expandIconWhite: { color: '#ffffff', fontSize: '1.5rem' },
    title: { fontSize: { xs: '0.75rem', md: '0.94rem' }, color: '#0d1117', fontWeight: 'bold' },
    titleWhite: { fontSize: { xs: '0.75rem', md: '0.94rem' }, color: '#ffffff', fontWeight: 'bold' }
  };

  // Handle left sidebar resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = e.clientX;
      const minWidth = 400;
      const maxWidth = 800;
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Handle right sidebar resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingRight) return;
      
      const newWidth = window.innerWidth - e.clientX;
      const minWidth = 300;
      const maxWidth = 600;
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setRightSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizingRight(false);
    };

    if (isResizingRight) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingRight]);

  // Validate all enabled panels and stock sheets whenever they change
  // Only validate non-empty values - empty values will be validated on blur
  useEffect(() => {
    // Validate panels (only non-empty values) using centralized validation
    const newInvalidPanels = {};
    panels.forEach((panel, idx) => {
      if (panel.enabled) {
        ['width', 'length', 'quantity'].forEach(field => {
          const fieldKey = `${idx}-${field}`;
          const value = panel[field];
          
          // Skip empty values - they'll be validated on blur
          if (value === '' || value === null) {
            return;
          }
          
          // Use centralized validation function
          const validation = validateNumericField(value, field, panel.enabled);
          if (!validation.isValid) {
            newInvalidPanels[fieldKey] = true;
          }
        });
        
        // Validate material type if different materials is enabled
        if (differentMaterials) {
          const materialTypeKey = `${idx}-materialType`;
          const materialValue = panel.materialType;
          
          // Skip empty values - they'll be validated on blur
          if (materialValue === '' || materialValue === null || materialValue === undefined) {
            // Don't mark as invalid during typing - will validate on blur
          } else {
            // Material type is not empty, so it's valid
            // (We don't need to add it to invalid list if it has a value)
          }
        }
      }
    });
    setInvalidPanelFields(prev => {
      // Merge with existing invalid fields (preserve blur-validated empty fields)
      return { ...prev, ...newInvalidPanels };
    });

    // Validate stock sheets (only non-empty values) using centralized validation
    const newInvalidStock = {};
    stockSheets.forEach((stock, idx) => {
      if (stock.enabled) {
        ['width', 'length', 'quantity'].forEach(field => {
          const fieldKey = `${idx}-${field}`;
          const value = stock[field];
          
          // Skip empty values - they'll be validated on blur
          if (value === '' || value === null) {
            return;
          }
          
          // Use centralized validation function
          const validation = validateNumericField(value, field, stock.enabled);
          if (!validation.isValid) {
            newInvalidStock[fieldKey] = true;
          }
        });
      }
    });
    setInvalidStockFields(prev => {
      // Merge with existing invalid fields (preserve blur-validated empty fields)
      return { ...prev, ...newInvalidStock };
    });
  }, [panels, stockSheets, differentMaterials]);

  // Panel table handlers - wrapper functions to call utility functions with state
  const handlePanelChange = (idx, field, value) => {
    handlePanelChangeUtil(idx, field, value, panels, setPanels, invalidPanelFields, setInvalidPanelFields, differentMaterials);
  };

  const handlePanelBlur = (idx, field) => {
    handlePanelBlurUtil(idx, field, panels, invalidPanelFields, setInvalidPanelFields, differentMaterials);
  };

  const togglePanelEnabled = (idx) => {
    togglePanelEnabledUtil(idx, panels, setPanels, invalidPanelFields, setInvalidPanelFields, differentMaterials);
  };

  const togglePanelGrain = (idx) => {
    togglePanelGrainUtil(idx, panels, setPanels);
  };

  const addPanel = () => addPanelUtil(panels, setPanels);
  const removePanel = (idx) => removePanelUtil(idx, panels, setPanels);

  // Stock sheet table handlers - wrapper functions to call utility functions with state
  const handleStockChange = (idx, field, value) => {
    handleStockChangeUtil(idx, field, value, stockSheets, setStockSheets, panels, setPanels, invalidStockFields, setInvalidStockFields);
  };

  const handleStockBlur = (idx, field) => {
    handleStockBlurUtil(idx, field, stockSheets, invalidStockFields, setInvalidStockFields);
  };

  const toggleStockEnabled = (idx) => {
    toggleStockEnabledUtil(idx, stockSheets, setStockSheets, invalidStockFields, setInvalidStockFields);
  };

  const toggleStockGrain = (idx) => {
    toggleStockGrainUtil(idx, stockSheets, setStockSheets);
  };

  const addStock = () => addStockUtil(stockSheets, setStockSheets, userSubscription, setUpgradeModalOpen);
  const removeStock = (idx) => removeStockUtil(idx, stockSheets, setStockSheets);

  // Menu state for panels and stock sheets tables
  const [panelsMenuAnchor, setPanelsMenuAnchor] = useState(null);
  const [stockMenuAnchor, setStockMenuAnchor] = useState(null);
  const panelsMenuOpen = Boolean(panelsMenuAnchor);
  const stockMenuOpen = Boolean(stockMenuAnchor);

  // Panels table menu handlers
  const handlePanelsMenuOpen = (event) => setPanelsMenuAnchor(event.currentTarget);
  const handlePanelsMenuClose = () => setPanelsMenuAnchor(null);
  
  const handleEnableAllPanels = () => {
    handleEnableAllPanelsUtil(panels, setPanels, handlePanelsMenuClose);
  };
  
  const handleDisableAllPanels = () => {
    handleDisableAllPanelsUtil(panels, setPanels, handlePanelsMenuClose);
  };
  
  const handleClearPanels = () => {
    handleClearPanelsUtil(setPanels, handlePanelsMenuClose);
  };
  
  const handleExportPanelsCsv = () => {
    handleExportPanelsCsvUtil(panels, handlePanelsMenuClose);
  };
  
  const handleImportPanelsCsv = (event) => {
    handleImportPanelsCsvUtil(event, setPanels, handlePanelsMenuClose);
  };

  // Stock sheets table menu handlers
  const handleStockMenuOpen = (event) => setStockMenuAnchor(event.currentTarget);
  const handleStockMenuClose = () => setStockMenuAnchor(null);
  
  const handleEnableAllStock = () => {
    handleEnableAllStockUtil(stockSheets, setStockSheets, handleStockMenuClose);
  };
  
  const handleDisableAllStock = () => {
    handleDisableAllStockUtil(stockSheets, setStockSheets, handleStockMenuClose);
  };
  
  const handleClearStock = () => {
    handleClearStockUtil(setStockSheets, handleStockMenuClose);
  };
  
  const handleExportStockCsv = () => {
    handleExportStockCsvUtil(stockSheets, handleStockMenuClose);
  };
  
  const handleImportStockCsv = (event) => {
    handleImportStockCsvUtil(event, setStockSheets, handleStockMenuClose);
  };


  const handleRun = async () => {
    await handleRunUtil(
      panels,
      stockSheets,
      kerfThickness,
      differentMaterials,
      labelsOnPanels,
      grainDirection,
      unitSystem,
      user,
      setOptimizationLoading,
      setOptimizationResults,
      setUpgradeModalOpen,
      db,
      currentProjectId
    );
  };

  // Menu and Modal state
  const [anchorEl, setAnchorEl] = useState(null);
  const [hamburgerAnchorEl, setHamburgerAnchorEl] = useState(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [optimizationResults, setOptimizationResults] = useState(null);
  const [globalStatisticsModalOpen, setGlobalStatisticsModalOpen] = useState(false);
  
  // Visualization settings
  const [vizFontSize, setVizFontSize] = useState(2);
  const [showDimensions, setShowDimensions] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [vizRotation, setVizRotation] = useState(0); // 0 or 90 degrees
  const [showCutOrder, setShowCutOrder] = useState(false); // Show cutting sequence overlay
  const [showRotationIndicator, setShowRotationIndicator] = useState(true); // Show rotation indicator on rotated panels

  // Save visualization preferences whenever they change (debounced to avoid too many writes)
  useEffect(() => {
    if (user && db) {
      // Use a timeout to debounce saves (save 500ms after last change)
      const timeoutId = setTimeout(() => {
        const preferences = {
          showDimensions,
          showLabels,
          vizRotation,
          showCutOrder,
          showRotationIndicator
        };
        saveUserVizPreferences(db, user, preferences);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [showDimensions, showLabels, vizRotation, showCutOrder, user, db]);

  // Update labels in optimization results when panel labels change
  useEffect(() => {
    // Use functional update to avoid dependency on optimizationResults
    setOptimizationResults(prevResults => {
      if (!prevResults || !prevResults.stock_sheets || !prevResults._panelIdMapping) {
        return prevResults;
      }

      // Create a map from panel ID to input panel using the stored mapping
      // This mapping was created at optimization time and never changes
      // Use the original row index to always map to the same row, regardless of enabled state
      const idToInputPanelMap = new Map();
      
      prevResults._panelIdMapping.forEach((mappedPanel) => {
        if (mappedPanel.ids && Array.isArray(mappedPanel.ids) && 
            mappedPanel._originalRowIndex !== undefined) {
          // Get the current panel from the same row position as optimization time
          const currentInputPanel = panels[mappedPanel._originalRowIndex];
          
          // Map each ID to the current input panel (if found)
          if (currentInputPanel) {
            mappedPanel.ids.forEach(id => {
              idToInputPanelMap.set(id, currentInputPanel);
            });
          }
        }
      });

      // Helper function to update a panel's label using the ID mapping
      const updatePanelLabel = (panel) => {
        const actualPanel = panel.panel || panel;
        const panelId = actualPanel.id;
        
        if (panelId === undefined || panelId === null) {
          return panel;
        }
        
        // Look up the current input panel by ID
        const matchingInputPanel = idToInputPanelMap.get(panelId);
        
        if (matchingInputPanel) {
          const currentLabel = actualPanel.label || '';
          const newLabel = matchingInputPanel.label || '';
          
          if (currentLabel !== newLabel) {
            // Update the label in the correct location (panel property or directly)
            if (panel.panel) {
              // Nested structure
              return {
                ...panel,
                panel: {
                  ...panel.panel,
                  label: newLabel
                }
              };
            } else {
              // Flat structure
              return {
                ...panel,
                label: newLabel
              };
            }
          }
        }
        
        return panel;
      };

      // Check if any labels need updating
      let hasChanges = false;
      
      // Update top-level panels array if it exists
      let updatedTopLevelPanels = prevResults.panels;
      if (prevResults.panels && Array.isArray(prevResults.panels)) {
        updatedTopLevelPanels = prevResults.panels.map((panel) => {
          const updated = updatePanelLabel(panel);
          if (updated !== panel) {
            hasChanges = true;
          }
          return updated;
        });
      }
      
      // Update panels in stock_sheets
      const updatedResults = {
        ...prevResults,
        panels: updatedTopLevelPanels,
        stock_sheets: prevResults.stock_sheets.map((stockSheet) => {
          if (!stockSheet.panels) return stockSheet;
          
          const updatedPanels = stockSheet.panels.map((resultPanel) => {
            const updated = updatePanelLabel(resultPanel);
            if (updated !== resultPanel) {
              hasChanges = true;
            }
            return updated;
          });
          
          return {
            ...stockSheet,
            panels: updatedPanels
          };
        })
      };

      return hasChanges ? updatedResults : prevResults;
    });
  }, [panels]);

  // Validation states - track invalid fields with red highlighting
  const [invalidPanelFields, setInvalidPanelFields] = useState({}); // { "0-width": true, "1-quantity": true, ... }
  const [invalidStockFields, setInvalidStockFields] = useState({}); // { "0-length": true, "1-quantity": true, ... }
  const [invalidKerfThickness, setInvalidKerfThickness] = useState(false);


  // Validation function to check if all inputs are valid before running optimization
  const isFormValid = () => {
    const validation = validateAllInputs(panels, stockSheets, kerfThickness, differentMaterials);
    return validation.isValid;
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleHamburgerMenuOpen = (event) => {
    setHamburgerAnchorEl(event.currentTarget);
  };

  const handleHamburgerMenuClose = () => {
    setHamburgerAnchorEl(null);
  };

  const handleSaveWithLoginCheck = () => {
    handleHamburgerMenuClose();
    if (user) {
      handleSaveModalOpen();
    } else {
      alert('You must log in to save or load projects');
    }
  };

  const handleLoadWithLoginCheck = () => {
    handleHamburgerMenuClose();
    if (user) {
      handleLoadModalOpen();
    } else {
      alert('You must log in to save or load projects');
    }
  };

  const handleLoginModalOpen = () => {
    setLoginModalOpen(true);
    handleMenuClose();
  };

  const handleLoginModalClose = () => {
    setLoginModalOpen(false);
  };


  // Project management state
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [saveInputName, setSaveInputName] = useState(''); // Separate state for save modal input
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [originalProjectName, setOriginalProjectName] = useState('');
  const [editingProjectName, setEditingProjectName] = useState(false);
  const [editingProjectValue, setEditingProjectValue] = useState('');
  const [projectSearchTerm, setProjectSearchTerm] = useState(''); // Search for projects in load modal

  const handleSaveModalOpen = () => {
    if (!user) {
      alert('Please log in to save projects');
      return;
    }
    // Pre-fill the modal input with current project name
    setSaveInputName(projectName);
    setSaveModalOpen(true);
  };

  const handleSaveModalClose = () => {
    setSaveModalOpen(false);
    setSaveInputName('');
  };

  const handleLoadModalOpen = () => {
    if (!user) {
      alert('Please log in to load projects');
      return;
    }
    // Projects are already loaded in the background
    setLoadModalOpen(true);
  };

  const handleLoadModalClose = () => {
    setLoadModalOpen(false);
    setProjectSearchTerm(''); // Reset search when closing modal
  };

  // Wrapper functions for project utilities
  const wrappedHandleSaveProject = async () => {
    // Ensure projects list is up-to-date before checking limit
    if (user) {
      await loadUserProjects(db, user, setSavedProjects);
    }
    
    // Check project limit for free users when creating new project
    // Default to 'free' tier if subscription status is not loaded
    const userTier = userSubscription?.tier || 'free';
    const isNewProject = !currentProjectId || (originalProjectName && saveInputName.trim() !== originalProjectName);
    
    if (isNewProject && userTier === 'free' && savedProjects.length >= 5) {
      alert('Free tier is limited to 5 projects. Upgrade to Premium for up to 100 projects!');
      setUpgradeModalOpen(true);
      return;
    }
    
    await handleSaveProject(
      db, user, saveInputName, panels, stockSheets, kerfThickness,
      labelsOnPanels, differentMaterials, grainDirection, unitSystem, optimizationResults,
      currentProjectId, originalProjectName, setCurrentProjectId, setProjectName,
      setOriginalProjectName, handleSaveModalClose, loadUserProjects
    );
    
    // Refresh the projects list after saving
    if (user) {
      await loadUserProjects(db, user, setSavedProjects);
    }
  };

  const wrappedHandleLoadProject = async (project) => await handleLoadProject(
    project, setPanels, setStockSheets, setKerfThickness, setLabelsOnPanels,
    setDifferentMaterials, setGrainDirection, setUnitSystem, setOptimizationResults,
    setCurrentProjectId, setProjectName, setOriginalProjectName, handleLoadModalClose,
    db, user
  );

  const wrappedHandleDeleteProject = (projectId, projectName) => handleDeleteProject(
    db, user, projectId, projectName, currentProjectId, setCurrentProjectId,
    setProjectName, setOriginalProjectName, loadUserProjects
  );

  const wrappedHandleNewProject = async () => {
    // Note: "New Project" just clears the current project, it doesn't create a saved project
    // So we don't need to check the limit here - the limit is checked when saving
    await handleNewProject(
      currentProjectId, setPanels, setStockSheets, setOptimizationResults,
      setCurrentProjectId, setProjectName, setOriginalProjectName, setEditingProjectName,
      db, user
    );
  };

  // Handle project title editing
  const handleProjectTitleClick = () => {
    setEditingProjectName(true);
    setEditingProjectValue(projectName || 'Untitled Project');
  };

  const handleProjectTitleSave = async () => {
    const newName = editingProjectValue.trim() || 'Untitled Project';
    setProjectName(newName);
    setSaveInputName(newName);
    setEditingProjectName(false);
    // Auto-save the project with new name
    if (user) {
      try {
        const projectData = {
          userId: user.uid,
          projectName: newName,
          panels: panels,
          stockSheets: stockSheets,
          settings: {
            kerfThickness: parseFloat(kerfThickness),
            labelsOnPanels,
            differentMaterials,
            grainDirection,
            unitSystem
          },
          lastOptimization: optimizationResults,
          updatedAt: serverTimestamp()
        };

        let finalProjectId = currentProjectId;
        if (currentProjectId) {
          // Update existing project
          await updateDoc(doc(db, 'projects', currentProjectId), projectData);
          setOriginalProjectName(newName);
        } else {
          // Create new project
          projectData.createdAt = serverTimestamp();
          const docRef = await addDoc(collection(db, 'projects'), projectData);
          finalProjectId = docRef.id;
          setCurrentProjectId(finalProjectId);
          setOriginalProjectName(newName);
        }
        // Save current project ID to user document
        await saveCurrentProjectId(db, user, finalProjectId);
        await loadUserProjects(db, user, setSavedProjects);
      } catch (error) {
        console.error('❌ Error saving project:', error);
        alert('Error saving project: ' + error.message);
      }
    }
  };

  const handleProjectTitleCancel = () => {
    setEditingProjectName(false);
  };

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleEmailSignUp = async () => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      // Don't manually set user - onAuthStateChanged will handle it
      setLoginModalOpen(false);
    } catch (error) {
      alert(error.message);
    }
  };


  
  // Responsive size for banner icon buttons
  // Material-UI IconButton defaults to size="medium" (40px button, 12px padding, 24px icon)
  // We override with explicit responsive values:
  // - fontSize controls the icon size (36px for xs, 24px for sm/md)
  // - padding controls the button container size
  const bannerIconButtonSx = {
    p: { xs: 0.5, sm: 0.75, md: 1 }, // Padding: xs=8px, sm=12px, md=16px
    mx: { xs: 0.25, sm: 0.375, md: 0.5 }, // Horizontal margin
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' },
    fontSize: { xs: '2.25rem', sm: '1.5rem', md: '1.5rem' }, // Icon size: xs=36px, sm/md=24px
    color: '#ffffff' // White icons on dark banner
  };

  return (
    <ThemeProvider theme={createAppTheme(themeMode)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        {/* Static Banner */}
        <AppBar position="static" sx={{ flexShrink: 0, zIndex: 1100 }}>
        <Toolbar sx={{ 
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          minHeight: { xs: 64, md: 64 },
          padding: { xs: '8px 12px', md: '8px 16px' },
          position: 'relative'
        }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mr: { xs: 1, md: 2 },
              userSelect: 'none',
              WebkitUserSelect: 'none'
            }}
            onMouseDown={preventSelection}
            onSelectStart={preventSelection}
          >
            <img 
              src={CutCreatorLogoDark} 
              alt="Cut Creator Logo" 
              style={{ 
                height: window.innerWidth < 600 ? 32 : 48, 
                marginRight: window.innerWidth < 600 ? 8 : 16,
                userSelect: 'none',
                pointerEvents: 'none'
              }} 
            />
            <Typography 
              variant="h4" 
              sx={{ 
                fontFamily: 'Roboto', 
                fontWeight: 900, 
                color: '#ffffff', // White text on dark banner
                letterSpacing: '0.1em',
                // fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                display: { xs: 'none', sm: 'block', md: 'block' },
                userSelect: 'none',
                WebkitUserSelect: 'none'
              }}
            >
              CUT CREATOR
            </Typography>
          </Box>

          {/* Centered Project Name */}
          <Box sx={{ 
            position: { xs: 'static', md: 'absolute' }, 
            left: { md: '50%' }, 
            transform: { md: 'translateX(-50%)' },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: { xs: '100%', md: '30%' },
            order: { xs: 3, md: 0 },
            mt: { xs: 1, md: 0 }
          }}>
            {editingProjectName ? (
              <TextField
                autoFocus
                value={editingProjectValue}
                onChange={(e) => setEditingProjectValue(e.target.value)}
                onBlur={handleProjectTitleSave}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleProjectTitleSave();
                  } else if (e.key === 'Escape') {
                    handleProjectTitleCancel();
                  }
                }}
                slotProps={{ htmlInput: { maxLength: 25 } }}
                sx={{
                  maxWidth: '100%',
                  '& .MuiOutlinedInput-root': {
                    padding: '4px 8px',
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': {
                      borderColor: '#ccc'
                    },
                    '&:hover fieldset': {
                      borderColor: '#999'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '0px 4px'
                  }
                }}
                placeholder="Project name"
              />
            ) : (
              <Box
                onClick={handleProjectTitleClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '& .edit-icon': {
                      display: 'block'
                    }
                  }
                }}
              >
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontStyle: currentProjectId ? 'normal' : 'italic',
                    opacity: currentProjectId ? 1 : 0.7,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    color: '#ffffff' // White text on dark banner
                  }}
                >
                  {projectName || 'Untitled Project'} {!currentProjectId && '(unsaved)'}
                </Typography>
                <Edit 
                  className="edit-icon"
                  sx={{ 
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    opacity: 0.7,
                    transition: 'opacity 0.2s',
                    display: 'none'
                  }} 
                />
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1 }, ml: { xs: 'auto', md: 'auto' }, order: { xs: 2, md: 0 } }}>
            {(!user || (user && userSubscription?.tier !== 'premium')) && (
              <Button
                color="inherit"
                onClick={() => setUpgradeModalOpen(true)}
                sx={{ 
                  textTransform: 'none', 
                  fontSize: { xs: '0.75rem', md: '1rem' },
                  padding: { xs: '4px 4px', md: '6px 6px' },
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center', 
                  gap: 1,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' }
                }}
              >
                <img src={UpgradeLogo} alt="Upgrade" style={{ height: window.innerWidth < 600 ? 16 : 24, width: window.innerWidth < 600 ? 16 : 24 }} />
                <span style={{ display: window.innerWidth < 600 ? 'none' : 'inline' }}>Upgrade</span>
              </Button>
            )}
            {user ? (
              <>
                <IconButton
                  color="inherit"
                  onClick={handleSaveModalOpen}
                  sx={bannerIconButtonSx}
                  title="Save Project"
                >
                  <Save />
                </IconButton>
                <IconButton
                  color="inherit"
                  onClick={handleMenuOpen}
                  sx={{ ...bannerIconButtonSx, position: 'relative' }}
                >
                  {userSubscription?.tier === 'premium' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: { xs: -6, md: -8 },
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: '#4caf50',
                        color: 'white',
                        px: { xs: 0.4, md: 0.5 },
                        py: { xs: 0.15, md: 0.2 },
                        borderRadius: 0.5,
                        fontSize: { xs: '0.4rem', md: '0.5rem' },
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: { xs: '0.3px', md: '0.5px' },
                        zIndex: 10,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
                      }}
                    >
                      PREMIUM
                    </Box>
                  )}
                  {user.photoURL ? (
                    <>
                      <Avatar 
                        src={user.photoURL} 
                        alt={user.displayName || 'User'} 
                        sx={{ 
                          width: { xs: 24, md: 32 },
                          height: { xs: 24, md: 32 }
                        }} 
                      />
                    <ArrowDropDown 
                      sx={{
                        position: 'absolute',
                        bottom: { xs: -6, md: -5 },
                        right: { xs: -6, md: -5 },
                        fontSize: { xs: '2rem', md: '2.5rem' },
                        color: 'white',
                        textShadow: '0 0 3px rgba(0,0,0,0.5)'
                      }}
                    />
                  </>
                ) : (
                  <>
                    <AccountCircle />
                    <ArrowDropDown 
                      sx={{
                        position: 'absolute',
                        bottom: -2,
                        right: -2,
                        fontSize: '1.5rem',
                        color: 'white',
                        textShadow: '0 0 3px rgba(0,0,0,0.5)'
                      }}
                    />
                  </>
                )}
                </IconButton>
              </>
            ) : (
              <Button
                color="inherit"
                onClick={handleLoginModalOpen}
                sx={{ 
                  textTransform: 'none',
                  fontSize: { xs: '0.75rem', md: '1rem' },
                  padding: { xs: '4px 4px', md: '6px 6px' },
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' }
                }}
              >
                Log In
              </Button>
            )}
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleHamburgerMenuOpen}
              sx={bannerIconButtonSx}
            >
              <MenuIcon />
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {userSubscription?.tier === 'premium' 
              ? <MenuItem onClick={() => { setManageSubscriptionModalOpen(true); handleMenuClose(); }} key="manage">Manage Subscription</MenuItem>
              : <MenuItem onClick={() => { setUpgradeModalOpen(true); handleMenuClose(); }} key="upgrade">Upgrade to Premium</MenuItem>
            }
            <MenuItem onClick={() => { handleLogout(auth, setUser); handleMenuClose(); }} key="logout">Logout</MenuItem>
          </Menu>
          <Menu
            anchorEl={hamburgerAnchorEl}
            open={Boolean(hamburgerAnchorEl)}
            onClose={handleHamburgerMenuClose}
          >
            <MenuItem onClick={() => { wrappedHandleNewProject(); handleHamburgerMenuClose(); }} key="new">New Project</MenuItem>
            <MenuItem onClick={handleSaveWithLoginCheck} key="save" startIcon={<Save />}>Save</MenuItem>
            <MenuItem onClick={handleLoadWithLoginCheck} key="load" startIcon={<FolderOpen />}>Load</MenuItem>
            <MenuItem onClick={() => { handleExportPDF(optimizationResults, projectName, kerfThickness, userSubscription); handleHamburgerMenuClose(); }} key="export" startIcon={<PictureAsPdf />}>Export PDF</MenuItem>
            <MenuItem 
              onClick={() => { handleExportSVG(optimizationResults, projectName, userSubscription, setUpgradeModalOpen); handleHamburgerMenuClose(); }} 
              key="export-svg"
              disabled={!user || userSubscription?.tier !== 'premium'}
            >
              Export SVG {(!user || userSubscription?.tier !== 'premium') && '🔒'}
            </MenuItem>
            <MenuItem 
              onClick={() => { handleExportDXF(optimizationResults, projectName, userSubscription, setUpgradeModalOpen); handleHamburgerMenuClose(); }} 
              key="export-dxf"
              disabled={!user || userSubscription?.tier !== 'premium'}
            >
              Export DXF {(!user || userSubscription?.tier !== 'premium') && '🔒'}
            </MenuItem>
            <MenuItem 
              onClick={() => { toggleTheme(); handleHamburgerMenuClose(); }} 
              key="theme"
            >
              {themeMode === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </MenuItem>
            <Divider />
            <MenuItem 
              onClick={() => { window.open('mailto:support@cut-creator.com', '_blank'); handleHamburgerMenuClose(); }} 
              key="support"
              startIcon={<Email />}
            >
              Contact Support
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Upgrade to Premium Modal */}
      <Modal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        aria-labelledby="upgrade-modal-title"
        aria-describedby="upgrade-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 420, md: 480 },
            maxWidth: '95vw',
            bgcolor: themeMode === 'dark' ? '#161b22' : '#ffffff',
            border: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de',
            boxShadow: 24,
            p: { xs: 2, md: 4 },
            borderRadius: 2,
            maxHeight: '90vh',
            overflow: 'auto'
          }}
        >
          <IconButton
            aria-label="close"
            onClick={() => setUpgradeModalOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: themeMode === 'dark' ? '#8b949e' : '#57606a',
              zIndex: 1,
              '&:hover': { color: themeMode === 'dark' ? '#ffffff' : '#24292f' }
            }}
          >
            <span style={{fontSize: 24, fontWeight: 'bold'}}>&times;</span>
          </IconButton>
          <Typography id="upgrade-modal-title" variant="h6" component="h2" gutterBottom align="center" sx={{ color: themeMode === 'dark' ? '#ffffff' : '#24292f' }}>
            Compare Plans & Upgrade
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, boxShadow: 'none' }}>
            <Table size="small" sx={{ minWidth: 420, '& .MuiTableCell-root': { color: themeMode === 'dark' ? '#ffffff' : '#24292f', borderColor: themeMode === 'dark' ? '#30363d' : '#d0d7de' } }}>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Free</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#fffbe6', color: '#bfa100' }}>Premium</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Calculations per day</TableCell>
                  <TableCell align="center">5</TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#fffbe6', color: '#bfa100' }}>Unlimited</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Active projects</TableCell>
                  <TableCell align="center">5</TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#fffbe6', color: '#bfa100' }}>100</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Stock sheets per project</TableCell>
                  <TableCell align="center">Up to 5</TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#fffbe6', color: '#bfa100' }}>Unlimited</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>PDF export</TableCell>
                  <TableCell align="center">Watermarked</TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#fffbe6', color: '#bfa100' }}>Yes</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>SVG file export</TableCell>
                  <TableCell align="center">No</TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#fffbe6', color: '#bfa100' }}>Yes</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>DXF file export</TableCell>
                  <TableCell align="center">No</TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#fffbe6', color: '#bfa100' }}>Yes</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Ads</TableCell>
                  <TableCell align="center">TBD</TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#fffbe6', color: '#bfa100' }}>No ads</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Free</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#fffbe6', color: '#bfa100' }}>
                    ${priceInfo.amount.toFixed(2)}/{priceInfo.interval}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell align="center"></TableCell>
                  <TableCell align="center">
                    {user ? (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ mt: 1, mb: 1, bgcolor: '#ffe066', color: '#bfa100', '&:hover': { bgcolor: '#ffe066' } }}
                        onClick={() => handleUpgrade(user, auth, setUpgradeLoading)}
                        disabled={upgradeLoading}
                      >
                        {upgradeLoading ? 'Redirecting...' : 'Upgrade'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ mt: 1, mb: 1, bgcolor: '#ffe066', color: '#bfa100', '&:hover': { bgcolor: '#ffe066' } }}
                        onClick={() => {
                          setUpgradeModalOpen(false);
                          handleLoginModalOpen();
                        }}
                      >
                        Log In
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal>

      {/* Manage Subscription Modal */}
      <Modal
        open={manageSubscriptionModalOpen}
        onClose={() => setManageSubscriptionModalOpen(false)}
        aria-labelledby="manage-subscription-modal-title"
        aria-describedby="manage-subscription-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 380, md: 420 },
            maxWidth: '95vw',
            bgcolor: themeMode === 'dark' ? '#161b22' : '#ffffff',
            border: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de',
            boxShadow: 24,
            p: { xs: 2, md: 4 },
            borderRadius: 2,
            maxHeight: '90vh',
            overflow: 'auto'
          }}
        >
          <IconButton
            aria-label="close"
            onClick={() => setManageSubscriptionModalOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: themeMode === 'dark' ? '#8b949e' : '#57606a',
              zIndex: 1,
              '&:hover': { color: themeMode === 'dark' ? '#ffffff' : '#24292f' }
            }}
          >
            <span style={{fontSize: 24, fontWeight: 'bold'}}>&times;</span>
          </IconButton>
          <Typography id="manage-subscription-modal-title" variant="h6" component="h2" gutterBottom align="center" sx={{ color: themeMode === 'dark' ? '#ffffff' : '#24292f' }}>
            Manage Subscription
          </Typography>
          
          {userSubscription && (
            <Box sx={{ mb: 3, mt: 2 }}>
              <Paper sx={{ p: 2, bgcolor: '#fffbe6' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#bfa100', mb: 1 }}>
                  Premium Plan
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5, color: '#bfa100' }}>
                  Status: <strong>{userSubscription.status}</strong>
                </Typography>
                {userSubscription.renewalDate && (
                  <Typography variant="body2" sx={{ color: '#bfa100' }}>
                    Renewal Date: <strong>{
                      userSubscription.renewalDate._seconds 
                        ? new Date(userSubscription.renewalDate._seconds * 1000).toLocaleDateString()
                        : new Date(userSubscription.renewalDate).toLocaleDateString()
                    }</strong>
                  </Typography>
                )}
              </Paper>
            </Box>
          )}

          <Typography variant="body2" sx={{ mb: 2, color: themeMode === 'dark' ? '#8b949e' : '#57606a' }}>
            You have access to all premium features including:
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: themeMode === 'dark' ? '#ffffff' : '#24292f' }}>✓ Unlimited calculations per day</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, color: themeMode === 'dark' ? '#ffffff' : '#24292f' }}>✓ Up to 100 active projects</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, color: themeMode === 'dark' ? '#ffffff' : '#24292f' }}>✓ Unlimited stock sheets per project</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, color: themeMode === 'dark' ? '#ffffff' : '#24292f' }}>✓ SVG & DXF file exports</Typography>
            <Typography variant="body2" sx={{ mb: 0.5, color: themeMode === 'dark' ? '#ffffff' : '#24292f' }}>✓ No watermarks on exports</Typography>
            <Typography variant="body2" sx={{ color: themeMode === 'dark' ? '#ffffff' : '#24292f' }}>✓ Ad-free experience</Typography>
          </Box>

          <Divider sx={{ mb: 2, borderColor: themeMode === 'dark' ? '#30363d' : '#d0d7de' }} />

          <Typography variant="body2" sx={{ mb: 2, color: themeMode === 'dark' ? '#8b949e' : '#57606a' }}>
            Need to cancel? Your premium features will remain active until the end of your billing period.
          </Typography>

          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={() => handleCancelSubscription(auth, setUserSubscription, setManageSubscriptionModalOpen, setCancelLoading)}
            disabled={cancelLoading}
          >
            {cancelLoading ? 'Canceling...' : 'Cancel Subscription'}
          </Button>
        </Box>
      </Modal>

      {/* Global Statistics Modal */}
      {optimizationResults && optimizationResults.global_statistics && (
        <Modal
          open={globalStatisticsModalOpen}
          onClose={() => setGlobalStatisticsModalOpen(false)}
          aria-labelledby="global-statistics-modal-title"
          aria-describedby="global-statistics-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 500, md: 600 },
              maxWidth: '95vw',
              bgcolor: themeMode === 'dark' ? '#161b22' : '#ffffff',
              border: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de',
              boxShadow: 24,
              p: { xs: 2, md: 4 },
              borderRadius: 2,
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <IconButton
              aria-label="close"
              onClick={() => setGlobalStatisticsModalOpen(false)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: themeMode === 'dark' ? '#8b949e' : '#57606a',
                zIndex: 1,
                '&:hover': { color: themeMode === 'dark' ? '#ffffff' : '#24292f' }
              }}
            >
              <span style={{fontSize: 24, fontWeight: 'bold'}}>&times;</span>
            </IconButton>
            <Typography id="global-statistics-modal-title" variant="h6" component="h2" gutterBottom align="center" sx={{ color: themeMode === 'dark' ? '#ffffff' : '#24292f', mb: 3 }}>
              Global Statistics
            </Typography>
            <TableContainer>
              <Table size="small" sx={{ 
                backgroundColor: themeMode === 'dark' ? '#0d1117' : '#ffffff',
                '& .MuiTableCell-root': { 
                  padding: '8px', 
                  fontSize: '0.875rem',
                  borderColor: themeMode === 'dark' ? '#30363d' : '#d0d7de',
                  color: themeMode === 'dark' ? '#ffffff' : '#24292f'
                }
              }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Sheet Usage</TableCell>
                    <TableCell>
                      {(() => {
                        const sheetBreakdown = {};
                        optimizationResults.stock_sheets?.forEach(stockSheet => {
                          const sheetSize = stockSheet.statistics?.sheet_size || {};
                          const key = `${sheetSize.width}x${sheetSize.length}-${stockSheet.statistics?.materialType || 'Unknown'}`;
                          if (!sheetBreakdown[key]) {
                            sheetBreakdown[key] = {
                              size: `${sheetSize.width}${getUnitSymbol(unitSystem)} × ${sheetSize.length}${getUnitSymbol(unitSystem)}`,
                              material: stockSheet.statistics?.materialType || 'Unknown',
                              quantity: 0
                            };
                          }
                          sheetBreakdown[key].quantity++;
                        });
                        const sheets = Object.values(sheetBreakdown);
                        return sheets.map((sheet, idx) => (
                          <div key={idx}>{sheet.quantity}× {sheet.size} ({sheet.material})</div>
                        ));
                      })()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Total Used Area</TableCell>
                    <TableCell>
                      {(() => {
                        const totalArea = optimizationResults.stock_sheets?.reduce((sum, stockSheet) => {
                          const sheetSize = stockSheet.statistics?.sheet_size || {};
                          return sum + (sheetSize.width * sheetSize.length);
                        }, 0) || 0;
                        const usedArea = optimizationResults.stock_sheets?.reduce((sum, stockSheet) => {
                          return sum + (stockSheet.panels?.reduce((pSum, p) => pSum + (p.width * p.length), 0) || 0);
                        }, 0) || 0;
                        return totalArea > 0 ? `${((usedArea / totalArea) * 100).toFixed(1)}%` : '0%';
                      })()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Total Wasted Area</TableCell>
                    <TableCell>
                      {(() => {
                        const totalArea = optimizationResults.stock_sheets?.reduce((sum, stockSheet) => {
                          const sheetSize = stockSheet.statistics?.sheet_size || {};
                          return sum + (sheetSize.width * sheetSize.length);
                        }, 0) || 0;
                        const usedArea = optimizationResults.stock_sheets?.reduce((sum, stockSheet) => {
                          return sum + (stockSheet.panels?.reduce((pSum, p) => pSum + (p.width * p.length), 0) || 0);
                        }, 0) || 0;
                        return totalArea > 0 ? `${(((totalArea - usedArea) / totalArea) * 100).toFixed(1)}%` : '0%';
                      })()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Total Panels</TableCell>
                    <TableCell>{optimizationResults.global_statistics?.total_panels || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Total Waste Panels</TableCell>
                    <TableCell>{optimizationResults.global_statistics?.total_waste_panels || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Total Cuts</TableCell>
                    <TableCell>{optimizationResults.global_statistics?.total_cuts || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Total Cut Length</TableCell>
                    <TableCell>{(optimizationResults.global_statistics?.total_cut_length || 0).toFixed(2)}{getUnitSymbol(unitSystem)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Kerf Thickness</TableCell>
                    <TableCell>{kerfThickness}{getUnitSymbol(unitSystem)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Modal>
      )}

      {/* Login Modal */}
      <Modal
        open={loginModalOpen}
        onClose={handleLoginModalClose}
        aria-labelledby="login-modal-title"
        aria-describedby="login-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 300, md: 300 },
            maxWidth: '95vw',
            bgcolor: themeMode === 'dark' ? '#161b22' : '#ffffff',
            border: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de',
            boxShadow: 24,
            p: { xs: 2, md: 4 },
            borderRadius: 2,
            maxHeight: '90vh',
            overflow: 'auto'
          }}
        >
          <Typography id="login-modal-title" variant="h6" component="h2" gutterBottom sx={{ color: themeMode === 'dark' ? '#ffffff' : '#24292f' }}>
            Log In
          </Typography>
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            label="Password"
            type="password"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleEmailLogin(auth, email, password, setLoginModalOpen);
              }
            }}
          />
          <Button
            variant="contained"
            fullWidth
            sx={{ mb: 2 }}
            onClick={() => handleEmailLogin(auth, email, password, setLoginModalOpen)}
          >
            Log In with Email
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleEmailSignUp}
          >
            Sign Up with Email
          </Button>
          <Typography variant="body2" align="center" sx={{ mt: 2, color: themeMode === 'dark' ? '#8b949e' : '#57606a' }}>
            Or log in with:
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
            <IconButton
              onClick={() => handleLogin(auth, googleProvider, setLoginModalOpen)}
              sx={{ borderRadius: '50%', border: '4px solid', borderColor: 'primary.main', color: 'primary.main', p: 1, '&:hover': { bgcolor: '#c0c0c0' } }}
            >
              <img src={GoogleLogo} alt="Google Logo" style={{ height: 24 }} />
            </IconButton>
            {/* Facebook login disabled - requires Facebook App Review for email permission
            <IconButton
              onClick={() => handleLogin(auth, facebookProvider, setLoginModalOpen)}
              sx={{ borderRadius: '50%', border: '4px solid', borderColor: 'primary.main', color: 'primary.main', p: 1, '&:hover': { bgcolor: '#c0c0c0' } }}
            >
              <img src={FacebookLogo} alt="Facebook Logo" style={{ height: 24 }} />
            </IconButton>
            */}
          </Box>
        </Box>
      </Modal>

      {/* Save Project Modal */}
      <Modal
        open={saveModalOpen}
        onClose={handleSaveModalClose}
        aria-labelledby="save-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 400, md: 400 },
            maxWidth: '95vw',
            bgcolor: themeMode === 'dark' ? '#161b22' : '#ffffff',
            border: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de',
            boxShadow: 24,
            p: { xs: 2, md: 4 },
            borderRadius: 2,
            maxHeight: '90vh',
            overflow: 'auto'
          }}
        >
          <Typography id="save-modal-title" variant="h6" component="h2" gutterBottom sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' }, color: themeMode === 'dark' ? '#ffffff' : '#24292f' }}>
            {currentProjectId ? 'Update Project' : 'Save New Project'}
          </Typography>
          
          {/* Show project count for free users */}
          {userSubscription?.tier === 'free' && !currentProjectId && (
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2, 
                color: savedProjects.length >= 5 ? 'error.main' : 'text.secondary',
                fontWeight: savedProjects.length >= 5 ? 'bold' : 'normal'
              }}
            >
              Projects: {savedProjects.length}/5 {savedProjects.length >= 5 && '(Limit reached - Upgrade for more)'}
            </Typography>
          )}
          
          <TextField
            label="Project Name"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={saveInputName}
            onChange={(e) => setSaveInputName(e.target.value)}
            placeholder="e.g., Kitchen Cabinets"
            slotProps={{ htmlInput: { maxLength: 25 } }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={wrappedHandleSaveProject}
            >
              {currentProjectId ? 'Update' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleSaveModalClose}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Load Project Modal */}
      <Modal
        open={loadModalOpen}
        onClose={handleLoadModalClose}
        aria-labelledby="load-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 500, md: 600 },
            maxWidth: '95vw',
            maxHeight: '80vh',
            bgcolor: themeMode === 'dark' ? '#161b22' : '#ffffff',
            border: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de',
            boxShadow: 24,
            p: { xs: 2, md: 4 },
            borderRadius: 2,
            overflow: 'auto',
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleLoadModalClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: themeMode === 'dark' ? '#8b949e' : '#57606a',
              zIndex: 1,
              '&:hover': { color: themeMode === 'dark' ? '#ffffff' : '#24292f' }
            }}
          >
            <span style={{fontSize: 24, fontWeight: 'bold'}}>&times;</span>
          </IconButton>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography id="load-modal-title" variant="h6" component="h2" sx={{ color: themeMode === 'dark' ? '#ffffff' : '#24292f' }}>
                Load Project
              </Typography>
              {userSubscription?.tier === 'free' && (
                <Typography variant="caption" sx={{ color: themeMode === 'dark' ? '#8b949e' : '#57606a' }}>
                  {savedProjects.length}/5 projects
                </Typography>
              )}
            </Box>
            <TextField
              placeholder="Search projects..."
              variant="outlined"
              size="small"
              sx={{ width: 250, mr: 3 }}
              value={projectSearchTerm}
              onChange={(e) => setProjectSearchTerm(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 25 } }}
            />
          </Box>
          {savedProjects.length === 0 ? (
            <Typography variant="body1" sx={{ my: 3, textAlign: 'center', color: themeMode === 'dark' ? '#8b949e' : '#57606a' }}>
              No saved projects found. Create and save a project to see it here.
            </Typography>
          ) : (() => {
            // Filter projects based on search term
            const filteredProjects = savedProjects.filter(project =>
              project.projectName.toLowerCase().includes(projectSearchTerm.toLowerCase())
            );
            
            return filteredProjects.length === 0 ? (
              <Typography variant="body1" sx={{ my: 3, textAlign: 'center', color: themeMode === 'dark' ? '#8b949e' : '#57606a' }}>
                No projects match "{projectSearchTerm}". Try a different search term.
              </Typography>
            ) : (
              <List>
                {filteredProjects.map((project) => (
                  <Box key={project.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: themeMode === 'dark' ? 'rgba(0, 217, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)' },
                        borderRadius: 1,
                      }}
                    >
                      <ListItemText
                        primary={project.projectName}
                        primaryTypographyProps={{ sx: { color: themeMode === 'dark' ? '#ffffff' : '#24292f' } }}
                        secondary={
                          <>
                            {project.updatedAt && new Date(project.updatedAt.seconds * 1000).toLocaleDateString()}{' '}
                            - {project.panels?.length || 0} panels, {project.stockSheets?.length || 0} stock sheets
                          </>
                        }
                        secondaryTypographyProps={{ sx: { color: themeMode === 'dark' ? '#8b949e' : '#57606a' } }}
                        onClick={() => wrappedHandleLoadProject(project)}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => wrappedHandleDeleteProject(project.id, project.projectName)}
                          sx={{ color: themeMode === 'dark' ? '#8b949e' : '#57606a', '&:hover': { color: themeMode === 'dark' ? '#ffffff' : '#24292f' } }}
                        >
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </Box>
                ))}
              </List>
            );
          })()}
        </Box>
      </Modal>


      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* Left Sidebar */}
        <Box sx={{ 
          width: { xs: '100%', md: `${sidebarWidth}px` }, 
          bgcolor: themeMode === 'dark' ? '#161b22' : '#f6f8fa', 
          borderRight: { xs: 'none', md: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de' }, 
          display: { xs: optimizationResults ? 'none' : 'block', md: 'flex' },
          flexDirection: 'column',
          flexShrink: 0,
          position: 'relative',
          height: '100%',
          overflow: 'hidden'
        }}>
          {/* Scrollable Content */}
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {/* Panels Table */}
          <Accordion defaultExpanded sx={accordionStyles.root}>
            <AccordionSummary
              expandIcon={<ArrowDropDown sx={accordionStyles.expandIcon} />}
              sx={accordionStyles.summaryWithMenu}
            >
              <Typography variant="h6" sx={accordionStyles.title}>Finished Panels</Typography>
              <IconButton 
                component="div"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePanelsMenuOpen(e);
                }} 
                size="small"
                sx={{ ml: 'auto', mr: 1, cursor: 'pointer', p: 0.5 }}
              >
                <MoreVert sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, pb: 2 }}>
              <Menu
                anchorEl={panelsMenuAnchor}
                open={panelsMenuOpen}
                onClose={handlePanelsMenuClose}
              >
                <MenuItem onClick={handleEnableAllPanels}>Enable All</MenuItem>
                <MenuItem onClick={handleDisableAllPanels}>Disable All</MenuItem>
                <MenuItem onClick={handleClearPanels}>Clear Table</MenuItem>
                <MenuItem onClick={handleExportPanelsCsv}>
                  <GetApp sx={{ mr: 1 }} fontSize="small" /> Export CSV
                </MenuItem>
                <MenuItem component="label">
                  <Publish sx={{ mr: 1 }} fontSize="small" /> Import CSV
                  <input type="file" accept=".csv" hidden onChange={handleImportPanelsCsv} />
                </MenuItem>
              </Menu>
              <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ 
            borderCollapse: 'collapse',
            width: '100%',
            backgroundColor: themeMode === 'dark' ? '#0d1117' : '#ffffff',
            '& .MuiTableCell-root': { 
              border: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de',
              padding: 0,
              fontSize: '0.75rem',
              height: 32
            },
            '& .MuiTextField-root': {
              width: '100%',
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
                backgroundColor: 'transparent',
                borderRadius: 0,
              },
              '& .MuiInputBase-input': {
                padding: '6px 8px',
                fontSize: '0.75rem',
                color: themeMode === 'dark' ? '#ffffff' : '#24292f'
              }
            },
            '& .MuiSelect-select': {
              padding: '6px 8px !important',
              fontSize: '0.75rem',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none'
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: 'rgba(0, 217, 255, 0.08)'
            }
          }}>
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-root': { backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa', fontWeight: 'bold', padding: '6px 8px', color: themeMode === 'dark' ? '#8b949e' : '#57606a' } }}>
                <TableCell sx={{ width: 40, textAlign: 'center' }}>✓</TableCell>
                {grainDirection && <TableCell sx={{ width: 40, textAlign: 'center' }}>Grain</TableCell>}
                <TableCell sx={{ width: 70 }}>Width</TableCell>
                <TableCell sx={{ width: 70 }}>Length</TableCell>
                <TableCell sx={{ width: 50 }}>Qty</TableCell>
                {differentMaterials && <TableCell sx={{ width: 100 }}>Material</TableCell>}
                {labelsOnPanels && <TableCell sx={{ width: 90 }}>Label</TableCell>}
                <TableCell sx={{ width: 36 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {panels.map((row, idx) => (
                <TableRow key={idx} sx={{ backgroundColor: row.enabled ? 'transparent' : (themeMode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)') }}>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Checkbox
                      checked={row.enabled}
                      onChange={() => togglePanelEnabled(idx)}
                      size="small"
                      sx={{ padding: '2px' }}
                    />
                  </TableCell>
                  {grainDirection && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      <IconButton onClick={() => togglePanelGrain(idx)} size="small" sx={{ padding: '2px' }}>
                        <img src={getGrainIcon(row.grainDirection, themeMode)} alt={row.grainDirection} style={{ height: 18, width: 18 }} />
                      </IconButton>
                    </TableCell>
                  )}
                  <TableCell sx={{ backgroundColor: invalidPanelFields[`${idx}-width`] ? 'rgba(211, 47, 47, 0.15)' : 'transparent' }}>
                    <TextField 
                      value={row.width} 
                      onChange={e => handlePanelChange(idx, 'width', e.target.value)}
                      onBlur={() => handlePanelBlur(idx, 'width')}
                      size="small"
                      slotProps={{ htmlInput: { maxLength: 25 } }}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: invalidPanelFields[`${idx}-length`] ? 'rgba(211, 47, 47, 0.15)' : 'transparent' }}>
                    <TextField 
                      value={row.length} 
                      onChange={e => handlePanelChange(idx, 'length', e.target.value)}
                      onBlur={() => handlePanelBlur(idx, 'length')}
                      size="small"
                      slotProps={{ htmlInput: { maxLength: 25 } }}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: invalidPanelFields[`${idx}-quantity`] ? 'rgba(211, 47, 47, 0.15)' : 'transparent' }}>
                    <TextField 
                      value={row.quantity} 
                      onChange={e => handlePanelChange(idx, 'quantity', e.target.value)}
                      onBlur={() => handlePanelBlur(idx, 'quantity')}
                      size="small" 
                      type="number"
                      slotProps={{ htmlInput: { min: 1, step: 1, maxLength: 25 } }}
                    />
                  </TableCell>
                  {differentMaterials && (
                    <TableCell sx={{ backgroundColor: invalidPanelFields[`${idx}-materialType`] ? 'rgba(211, 47, 47, 0.15)' : 'transparent' }}>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={row.materialType || ''}
                          onChange={e => handlePanelChange(idx, 'materialType', e.target.value)}
                          onBlur={() => handlePanelBlur(idx, 'materialType')}
                          displayEmpty
                          sx={{ fontSize: '0.75rem' }}
                        >
                          <MenuItem value="" sx={{ fontSize: '0.75rem' }}><em>Select</em></MenuItem>
                          {getMaterialTypes(stockSheets).map((material, i) => (
                            <MenuItem key={i} value={material} sx={{ fontSize: '0.75rem' }}>{material}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  )}
                  {labelsOnPanels && (
                    <TableCell>
                      <TextField value={row.label} onChange={e => handlePanelChange(idx, 'label', e.target.value)} size="small" slotProps={{ htmlInput: { maxLength: 25 } }} />
                    </TableCell>
                  )}
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton onClick={() => removePanel(idx)} disabled={panels.length === 1} size="small" sx={{ padding: '2px' }}>
                      <Delete sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell 
                  colSpan={
                    2 + (grainDirection ? 1 : 0) + (differentMaterials ? 1 : 0) + (labelsOnPanels ? 1 : 0) + 3
                  } 
                  sx={{ backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa', padding: '4px 8px !important' }}
                >
                  <Button startIcon={<Add />} onClick={addPanel} size="small" sx={{ fontSize: '0.75rem' }}>Add Panel</Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* Stock Sheets Table */}
          <Accordion defaultExpanded sx={accordionStyles.root}>
            <AccordionSummary
              expandIcon={<ArrowDropDown sx={accordionStyles.expandIcon} />}
              sx={accordionStyles.summaryWithMenu}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={accordionStyles.title}>Stock Sheets</Typography>
                {userSubscription?.tier === 'free' && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: stockSheets.length >= 5 ? 'error.main' : 'text.secondary',
                      fontWeight: stockSheets.length >= 5 ? 'bold' : 'normal',
                      bgcolor: stockSheets.length >= 5 ? 'error.light' : 'grey.200',
                      px: { xs: 0.5, md: 1 },
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: { xs: '0.65rem', md: '0.75rem' }
                    }}
                  >
                    {stockSheets.length}/5
                  </Typography>
                )}
              </Box>
              <IconButton 
                component="div"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStockMenuOpen(e);
                }} 
                size="small"
                sx={{ ml: 'auto', mr: 1, cursor: 'pointer', p: 0.5 }}
              >
                <MoreVert sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, pb: 2 }}>
              <Menu
                anchorEl={stockMenuAnchor}
                open={stockMenuOpen}
                onClose={handleStockMenuClose}
              >
                <MenuItem onClick={handleEnableAllStock}>Enable All</MenuItem>
                <MenuItem onClick={handleDisableAllStock}>Disable All</MenuItem>
                <MenuItem onClick={handleClearStock}>Clear Table</MenuItem>
                <MenuItem onClick={handleExportStockCsv}>
                  <GetApp sx={{ mr: 1 }} fontSize="small" /> Export CSV
                </MenuItem>
                <MenuItem component="label">
                  <Publish sx={{ mr: 1 }} fontSize="small" /> Import CSV
                  <input type="file" accept=".csv" hidden onChange={handleImportStockCsv} />
                </MenuItem>
              </Menu>
              <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ 
              borderCollapse: 'collapse',
              width: '100%',
              backgroundColor: themeMode === 'dark' ? '#0d1117' : '#ffffff',
              '& .MuiTableCell-root': { 
                border: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de',
                padding: 0,
                fontSize: '0.75rem',
                height: 32
              },
              '& .MuiTextField-root': {
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  backgroundColor: 'transparent',
                  borderRadius: 0,
                },
                '& .MuiInputBase-input': {
                  padding: '6px 8px',
                  fontSize: '0.75rem',
                  color: themeMode === 'dark' ? '#ffffff' : '#24292f'
                }
              },
              '& .MuiSelect-select': {
                padding: '6px 8px !important',
                fontSize: '0.75rem',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '& .MuiTableRow-root:hover': {
                backgroundColor: 'rgba(0, 217, 255, 0.08)'
              }
            }}>
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-root': { backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa', fontWeight: 'bold', padding: '6px 8px', color: themeMode === 'dark' ? '#8b949e' : '#57606a' } }}>
                  <TableCell sx={{ width: 40, textAlign: 'center' }}>✓</TableCell>
                  {grainDirection && <TableCell sx={{ width: 40, textAlign: 'center' }}>Grain</TableCell>}
                  <TableCell sx={{ width: 70 }}>Width</TableCell>
                  <TableCell sx={{ width: 70 }}>Length</TableCell>
                  <TableCell sx={{ width: 50 }}>Qty</TableCell>
                  {differentMaterials && <TableCell sx={{ width: 100 }}>Material</TableCell>}
                  {labelsOnPanels && <TableCell sx={{ width: 90 }}></TableCell>}
                  <TableCell sx={{ width: 36 }}></TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {stockSheets.map((row, idx) => (
                <TableRow key={idx} sx={{ backgroundColor: row.enabled ? 'transparent' : (themeMode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)') }}>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Checkbox
                      checked={row.enabled}
                      onChange={() => toggleStockEnabled(idx)}
                      size="small"
                      sx={{ padding: '2px' }}
                    />
                  </TableCell>
                  {grainDirection && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      <IconButton onClick={() => toggleStockGrain(idx)} size="small" sx={{ padding: '2px' }}>
                        <img src={getGrainIcon(row.grainDirection, themeMode)} alt={row.grainDirection} style={{ height: 18, width: 18 }} />
                      </IconButton>
                    </TableCell>
                  )}
                  <TableCell sx={{ backgroundColor: invalidStockFields[`${idx}-width`] ? 'rgba(211, 47, 47, 0.15)' : 'transparent' }}>
                    <TextField 
                      value={row.width} 
                      onChange={e => handleStockChange(idx, 'width', e.target.value)}
                      onBlur={() => handleStockBlur(idx, 'width')}
                      placeholder={idx === 0 ? '96' : ''} 
                      size="small"
                      slotProps={{ htmlInput: { maxLength: 25 } }}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: invalidStockFields[`${idx}-length`] ? 'rgba(211, 47, 47, 0.15)' : 'transparent' }}>
                    <TextField 
                      value={row.length} 
                      onChange={e => handleStockChange(idx, 'length', e.target.value)}
                      onBlur={() => handleStockBlur(idx, 'length')}
                      placeholder={idx === 0 ? '48' : ''} 
                      size="small"
                      slotProps={{ htmlInput: { maxLength: 25 } }}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: invalidStockFields[`${idx}-quantity`] ? 'rgba(211, 47, 47, 0.15)' : 'transparent' }}>
                    <TextField 
                      value={row.quantity} 
                      onChange={e => handleStockChange(idx, 'quantity', e.target.value)}
                      onBlur={() => handleStockBlur(idx, 'quantity')}
                      placeholder={idx === 0 ? '1' : ''} 
                      size="small"
                      type="number"
                      slotProps={{ htmlInput: { min: 1, step: 1, maxLength: 25 } }}
                    />
                  </TableCell>
                  {differentMaterials && (
                    <TableCell>
                      <TextField value={row.materialType} onChange={e => handleStockChange(idx, 'materialType', e.target.value)} placeholder={idx === 0 ? '3/4 Plywood' : ''} size="small" slotProps={{ htmlInput: { maxLength: 25 } }} />
                    </TableCell>
                  )}
                  {labelsOnPanels && (
                    <TableCell></TableCell>
                  )}
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton onClick={() => removeStock(idx)} disabled={stockSheets.length === 1} size="small" sx={{ padding: '2px' }}>
                      <Delete sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell 
                  colSpan={
                    2 + (grainDirection ? 1 : 0) + (differentMaterials ? 1 : 0) + (labelsOnPanels ? 1 : 0) + 3
                  } 
                  sx={{ backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa', padding: '4px 8px !important' }}
                >
                  <Button 
                    startIcon={<Add />} 
                    onClick={addStock}
                    disabled={userSubscription?.tier === 'free' && stockSheets.length >= 5}
                    size="small"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    Add Stock Sheet
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* Project Settings */}
          <Accordion sx={accordionStyles.root}>
            <AccordionSummary
              expandIcon={<ArrowDropDown sx={accordionStyles.expandIcon} />}
              sx={accordionStyles.summary}
            >
              <Typography variant="h6" sx={accordionStyles.title}>Project Settings</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2, pt: 1, fontSize: '0.75rem' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 2 }}>
                <Typography sx={{ minWidth: '120px', color: themeMode === 'dark' ? '#ffffff' : '#24292f', fontWeight: 500, fontSize: '0.75rem' }}>
                  Kerf Thickness ({getUnitSymbol(unitSystem)})
                </Typography>
                <TextField
                  type="number"
                  variant="outlined"
                  size="small"
                  sx={{ 
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderColor: invalidKerfThickness ? 'error.main' : 'inherit',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused': {
                      borderColor: invalidKerfThickness ? 'error.main' : 'primary.main',
                    }
                  }}
                  error={invalidKerfThickness}
                  helperText={invalidKerfThickness ? 'Kerf thickness must be 0 or greater' : ''}
                  value={kerfThickness}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Filter input to only allow valid numeric characters (digits and decimal point)
                    const filteredValue = filterNumericInput(value, 'width'); // Use width pattern for decimal numbers
                    
                    // Update state with filtered value
                    setKerfThickness(filteredValue);
                    
                    // Validate using centralized validation function
                    const validation = validateKerfThickness(filteredValue);
                    setInvalidKerfThickness(!validation.isValid);
                  }}
                  onBlur={() => {
                    // Validate on blur to catch empty values
                    const validation = validateKerfThickness(kerfThickness);
                    setInvalidKerfThickness(!validation.isValid);
                  }}
                  slotProps={{
                    htmlInput: {
                      step: '0.001',
                      min: '0'
                    }
                  }}
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={labelsOnPanels}
                    onChange={(e) => setLabelsOnPanels(e.target.checked)}
                    size="small"
                  />
                }
                label="Labels on Panels"
                sx={{ mb: 1.5, display: 'block', '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={differentMaterials}
                    onChange={(e) => setDifferentMaterials(e.target.checked)}
                    size="small"
                  />
                }
                label="Different Materials"
                sx={{ mb: 1.5, display: 'block', '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={grainDirection}
                    onChange={(e) => setGrainDirection(e.target.checked)}
                    size="small"
                  />
                }
                label="Grain Direction"
                sx={{ mb: 1.5, display: 'block', '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
              />

              <FormControl fullWidth sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ mb: 0.5, color: '#8b949e', fontSize: '0.75rem' }}>Unit System</Typography>
                <Select
                  value={unitSystem}
                  onChange={(e) => setUnitSystem(e.target.value)}
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                >
                  <MenuItem value="imperial" sx={{ fontSize: '0.75rem' }}>Inches (")</MenuItem>
                  <MenuItem value="meters" sx={{ fontSize: '0.75rem' }}>Meters (m)</MenuItem>
                  <MenuItem value="metric" sx={{ fontSize: '0.75rem' }}>Centimeters (cm)</MenuItem>
                  <MenuItem value="millimeters" sx={{ fontSize: '0.75rem' }}>Millimeters (mm)</MenuItem>
                </Select>
              </FormControl>
            </AccordionDetails>
          </Accordion>


          {/* Unplaced Panels Warning */}
          {optimizationResults && optimizationResults.unplaced_panels && optimizationResults.unplaced_panels.length > 0 && (
            <Accordion defaultExpanded sx={accordionStyles.root}>
              <AccordionSummary
                expandIcon={<ArrowDropDown sx={accordionStyles.expandIconWhite} />}
                sx={accordionStyles.summaryRed}
              >
                <Typography variant="h6" sx={accordionStyles.titleWhite}>
                  ⚠️ Unplaced Panels ({optimizationResults.unplaced_panels.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pb: 2, pt: 1 }}>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {(() => {
                    const groupedPanels = new Map();
                    optimizationResults.unplaced_panels.forEach(panel => {
                      const key = `${panel.width}-${panel.length}-${panel.label || ''}-${panel.materialType || ''}`;
                      if (groupedPanels.has(key)) {
                        groupedPanels.get(key).quantity += 1;
                      } else {
                        groupedPanels.set(key, { width: panel.width, length: panel.length, label: panel.label, materialType: panel.materialType, quantity: 1 });
                      }
                    });
                    return Array.from(groupedPanels.values()).map((panel, idx) => (
                      <Typography component="li" key={idx} variant="body2" sx={{ mb: 0.5, color: '#ffffff' }}>
                        <strong>{panel.width}{getUnitSymbol(unitSystem)} × {panel.length}{getUnitSymbol(unitSystem)}</strong>
                        {panel.label && ` - ${panel.label}`}
                        {panel.quantity > 1 && ` × ${panel.quantity}`}
                      </Typography>
                    ));
                  })()}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
          </Box>

          {/* Run Button - Anchored to bottom */}
          <Box sx={{ px: 2, py: 2, borderTop: '1px solid #30363d', flexShrink: 0 }}>
            <Button 
              variant="contained" 
              size="large" 
              onClick={handleRun} 
              disabled={optimizationLoading || !isFormValid()}
              fullWidth
              sx={{ 
                bgcolor: `${themeMode === 'dark' ? '#00ff88' : '#00cc6a'} !important`,
                backgroundColor: `${themeMode === 'dark' ? '#00ff88' : '#00cc6a'} !important`,
                color: '#0d1117 !important',
                fontWeight: 800,
                fontSize: '0.9rem',
                py: 2,
                textTransform: 'uppercase',
                '&:hover': { 
                  bgcolor: `${themeMode === 'dark' ? '#00e677' : '#00b85c'} !important`,
                  backgroundColor: `${themeMode === 'dark' ? '#00e677' : '#00b85c'} !important`,
                  boxShadow: themeMode === 'dark'
                    ? '0 6px 20px rgba(0, 255, 136, 0.5), 0 4px 8px rgba(0, 0, 0, 0.4)'
                    : '0 6px 20px rgba(0, 204, 106, 0.4), 0 4px 8px rgba(0, 0, 0, 0.15)'
                },
                borderRadius: 1,
                letterSpacing: '1px',
                position: 'relative',
                boxShadow: themeMode === 'dark' 
                  ? '0 6px 16px rgba(0, 255, 136, 0.5), 0 3px 6px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)' 
                  : '0 6px 16px rgba(0, 204, 106, 0.4), 0 3px 6px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                transition: 'all 0.2s ease-in-out',
                '&:active': {
                  boxShadow: themeMode === 'dark'
                    ? '0 3px 8px rgba(0, 255, 136, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3)'
                    : '0 3px 8px rgba(0, 204, 106, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1)'
                },
                '&:disabled': {
                  bgcolor: `${themeMode === 'dark' ? '#30363d' : '#d0d7de'} !important`,
                  backgroundColor: `${themeMode === 'dark' ? '#30363d' : '#d0d7de'} !important`,
                  color: `${themeMode === 'dark' ? '#8b949e' : '#57606a'} !important`,
                  boxShadow: 'none'
                },
                '&.MuiButton-containedPrimary': {
                  bgcolor: `${themeMode === 'dark' ? '#00ff88' : '#00cc6a'} !important`,
                  backgroundColor: `${themeMode === 'dark' ? '#00ff88' : '#00cc6a'} !important`,
                }
              }}
            >
              {optimizationLoading && (
                <CircularProgress 
                  size={24} 
                  sx={{ 
                    position: 'absolute',
                    left: '20px',
                    color: '#ffffff'
                  }} 
                />
              )}
              {optimizationLoading ? 'Processing...' : 'RUN OPTIMIZATION'}
            </Button>
          </Box>

        </Box>

        {/* Resize Handle */}
        <Box
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
          }}
          sx={{
            display: { xs: 'none', md: 'block' },
            width: '4px',
            bgcolor: isResizing ? '#00d9ff' : '#30363d',
            cursor: 'col-resize',
            flexShrink: 0,
            '&:hover': {
              bgcolor: '#00d9ff',
            },
            transition: 'background-color 0.2s',
            userSelect: 'none',
            zIndex: 10
          }}
        />
        
        {/* Right Content Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: themeMode === 'dark' ? '#0d1117' : '#ffffff', minWidth: 0 }}>
          {/* Optimization Results */}
          {optimizationResults && (
            <>
              {/* Visualization Toolbar */}
              <Box sx={{
                height: 36,
                bgcolor: themeMode === 'dark' ? '#161b22' : '#f6f8fa',
                borderBottom: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2
              }}>
                {/* Increase Font Size */}
                <Box sx={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: themeMode === 'dark' ? '#0d1117' : '#ffffff',
                  borderRadius: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: themeMode === 'dark' ? 'none' : '1px solid #d0d7de',
                  '&:hover': { bgcolor: themeMode === 'dark' ? '#1f2937' : '#f6f8fa' }
                }} 
                title="Increase Font Size"
                onClick={() => setVizFontSize(prev => Math.min(prev + 0.5, 10))}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: themeMode === 'dark' ? '#8b949e' : '#57606a' }}>A+</Typography>
                </Box>
                
                {/* Decrease Font Size */}
                <Box sx={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: themeMode === 'dark' ? '#0d1117' : '#ffffff',
                  borderRadius: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: themeMode === 'dark' ? 'none' : '1px solid #d0d7de',
                  '&:hover': { bgcolor: themeMode === 'dark' ? '#1f2937' : '#f6f8fa' }
                }} 
                title="Decrease Font Size"
                onClick={() => setVizFontSize(prev => Math.max(prev - 0.5, 1))}>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', color: themeMode === 'dark' ? '#8b949e' : '#57606a' }}>A-</Typography>
                </Box>
                
                {/* Toggle Dimensions */}
                <Box sx={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: showDimensions ? 'rgba(0, 217, 255, 0.16)' : (themeMode === 'dark' ? '#0d1117' : '#ffffff'),
                  borderRadius: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: themeMode === 'dark' ? 'none' : '1px solid #d0d7de',
                  '&:hover': { bgcolor: showDimensions ? 'rgba(0, 217, 255, 0.24)' : (themeMode === 'dark' ? '#1f2937' : '#f6f8fa') }
                }} 
                title="Toggle Dimensions"
                onClick={() => setShowDimensions(prev => !prev)}>
                  <DimensionsIcon style={{ width: 20, height: 20, fill: showDimensions ? '#00d9ff' : (themeMode === 'dark' ? '#8b949e' : '#57606a'), color: showDimensions ? '#00d9ff' : (themeMode === 'dark' ? '#8b949e' : '#57606a') }} />
                </Box>
                
                {/* Toggle Labels */}
                <Box sx={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: showLabels ? 'rgba(0, 217, 255, 0.16)' : (themeMode === 'dark' ? '#0d1117' : '#ffffff'),
                  borderRadius: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: themeMode === 'dark' ? 'none' : '1px solid #d0d7de',
                  '&:hover': { bgcolor: showLabels ? 'rgba(0, 217, 255, 0.24)' : (themeMode === 'dark' ? '#1f2937' : '#f6f8fa') }
                }} 
                title="Toggle Labels"
                onClick={() => setShowLabels(prev => !prev)}>
                  <TagIcon style={{ width: 20, height: 20, fill: showLabels ? '#00d9ff' : (themeMode === 'dark' ? '#8b949e' : '#57606a'), color: showLabels ? '#00d9ff' : (themeMode === 'dark' ? '#8b949e' : '#57606a') }} />
                </Box>
                
                {/* Toggle Rotation Indicator */}
                <Box sx={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: showRotationIndicator ? 'rgba(0, 217, 255, 0.16)' : (themeMode === 'dark' ? '#0d1117' : '#ffffff'),
                  borderRadius: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: themeMode === 'dark' ? 'none' : '1px solid #d0d7de',
                  '&:hover': { bgcolor: showRotationIndicator ? 'rgba(0, 217, 255, 0.24)' : (themeMode === 'dark' ? '#1f2937' : '#f6f8fa') }
                }} 
                title="Toggle Rotation Indicator (shows when panels have been rotated 90°)"
                onClick={() => setShowRotationIndicator(prev => !prev)}>
                  <RotationIndicatorIcon style={{ width: 20, height: 20, fill: showRotationIndicator ? '#00d9ff' : (themeMode === 'dark' ? '#8b949e' : '#57606a'), color: showRotationIndicator ? '#00d9ff' : (themeMode === 'dark' ? '#8b949e' : '#57606a') }} />
                </Box>
                
                {/* Toggle Cut Order */}
                <Box sx={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: showCutOrder ? 'rgba(0, 217, 255, 0.16)' : (themeMode === 'dark' ? '#0d1117' : '#ffffff'),
                  borderRadius: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: themeMode === 'dark' ? 'none' : '1px solid #d0d7de',
                  '&:hover': { bgcolor: showCutOrder ? 'rgba(0, 217, 255, 0.24)' : (themeMode === 'dark' ? '#1f2937' : '#f6f8fa') }
                }} 
                title="Toggle Cut Order"
                onClick={() => setShowCutOrder(prev => !prev)}>
                  <ChecklistIcon style={{ width: 20, height: 20, fill: showCutOrder ? '#00d9ff' : (themeMode === 'dark' ? '#8b949e' : '#57606a'), color: showCutOrder ? '#00d9ff' : (themeMode === 'dark' ? '#8b949e' : '#57606a') }} />
                </Box>

                {/* Rotate View */}
                <Box sx={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: vizRotation === 90 ? 'rgba(0, 217, 255, 0.16)' : (themeMode === 'dark' ? '#0d1117' : '#ffffff'),
                  borderRadius: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: themeMode === 'dark' ? 'none' : '1px solid #d0d7de',
                  '&:hover': { bgcolor: vizRotation === 90 ? 'rgba(0, 217, 255, 0.24)' : (themeMode === 'dark' ? '#1f2937' : '#f6f8fa') }
                }} 
                title="Rotate View (0° / 90°)"
                onClick={() => setVizRotation(prev => prev === 0 ? 90 : 0)}>
                  <RotateViewIcon style={{ width: 20, height: 20, fill: vizRotation === 90 ? '#00d9ff' : (themeMode === 'dark' ? '#8b949e' : '#57606a'), color: vizRotation === 90 ? '#00d9ff' : (themeMode === 'dark' ? '#8b949e' : '#57606a') }} />
                </Box>

                {/* Global Statistics - Right-justified */}
                {optimizationResults && optimizationResults.global_statistics && (
                  <Button
                    onClick={() => setGlobalStatisticsModalOpen(true)}
                    sx={{
                      ml: 'auto',
                      height: 28,
                      px: 1.5,
                      py: 0.5,
                      fontSize: '1.0rem',
                      fontWeight: 'normal',
                      textTransform: 'none',
                      bgcolor: themeMode === 'dark' ? '#0d1117' : '#ffffff',
                      color: themeMode === 'dark' ? '#8b949e' : '#57606a',
                      border: themeMode === 'dark' ? 'none' : '1px solid #d0d7de',
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: themeMode === 'dark' ? '#1f2937' : '#f6f8fa',
                        color: themeMode === 'dark' ? '#ffffff' : '#24292f'
                      }
                    }}
                  >
                    Global Statistics
                  </Button>
                )}
              </Box>

              {/* Visualization Area - Full-page scroll sections */}
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto', 
                scrollSnapType: { xs: 'none', md: 'y mandatory' },
                minHeight: 0
              }}>
                {/* Full-Page Sheet Sections */}
                {optimizationResults.stock_sheets && Array.isArray(optimizationResults.stock_sheets) && 
                  optimizationResults.stock_sheets.map((stockSheet, index) => {
                    if (!stockSheet) return null;
                    const quantity = stockSheet.statistics?.quantity || 1;
                    const sheetIndices = stockSheet.statistics?.sheet_indices || [index + 1];
                    const statistics = stockSheet.statistics || {};
                    const sheetSize = statistics?.sheet_size || {};
                    const panelsArray = stockSheet.panels || [];
                    const wastePanels = stockSheet.waste_panels || [];
                    const numWastePanels = wastePanels.length;
                    const totalArea = (sheetSize.width || 0) * (sheetSize.length || 0);
                    const usedArea = panelsArray.reduce((sum, p) => sum + (p.width * p.length), 0);
                    const usedPercentage = totalArea > 0 ? ((usedArea / totalArea) * 100).toFixed(1) : '0';
                    const wastedPercentage = totalArea > 0 ? (((totalArea - usedArea) / totalArea) * 100).toFixed(1) : '0';
                    const cuttingSequence = stockSheet.cutlist || [];
                    
                    const getLabelForPanelId = (panelId) => {
                      if (panelId !== undefined && panelId !== null && panels.length > 0) {
                        const inputPanel = panels.find(p => p.ids && Array.isArray(p.ids) && p.ids.includes(panelId));
                        if (inputPanel && inputPanel.label) return inputPanel.label;
                      }
                      if (panelId !== undefined && panelId !== null && panelsArray.length > 0) {
                        const responsePanel = panelsArray.find(p => p.id === panelId);
                        if (responsePanel && responsePanel.label) return responsePanel.label;
                      }
                      return `Panel ${panelId}`;
                    };
                    
                    return (
                      <Box 
                        key={index} 
                        sx={{ 
                          minHeight: { xs: 'auto', md: 'calc(100vh - 64px)' },
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          p: 2,
                          gap: 2
                        }}
                      >
                        {/* Left: Visualizer */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <Box sx={{ position: 'relative', scrollSnapAlign: { xs: 'none', md: 'start' } }}>
                            {quantity > 1 && (
                              <Box sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#4caf50', color: 'white', px: 2, py: 1, borderRadius: 2, fontWeight: 'bold', fontSize: '1rem', zIndex: 5, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                                ×{quantity} sheets
                              </Box>
                            )}
                            <Box data-sheet-index={sheetIndices[0] - 1}>
                              <CutLayoutVisualization 
                                stockSheet={stockSheet} 
                                index={index}
                                fontSize={vizFontSize}
                                showDimensions={showDimensions}
                                showLabels={showLabels}
                                rotation={vizRotation}
                                showCutOrder={showCutOrder}
                                showRotationIndicator={showRotationIndicator}
                                unitSystem={unitSystem}
                                hoveredCut={hoveredCuts[index] !== undefined ? hoveredCuts[index] : null}
                                onHoveredCutChange={(cutIndex) => setHoveredCuts(prev => {
                                  if (cutIndex === null) { const newState = { ...prev }; delete newState[index]; return newState; }
                                  return { ...prev, [index]: cutIndex };
                                })}
                                themeMode={themeMode}
                                responsePanels={(() => {
                                  let panelsList = optimizationResults?.panels || [];
                                  if (panelsList.length === 0 && optimizationResults?.stock_sheets) {
                                    const panelMap = new Map();
                                    optimizationResults.stock_sheets.forEach(sheet => {
                                      if (sheet.panels && Array.isArray(sheet.panels)) {
                                        sheet.panels.forEach(panel => {
                                          if (panel.id !== undefined && panel.id !== null && !panelMap.has(panel.id)) {
                                            panelMap.set(panel.id, panel);
                                          }
                                        });
                                      }
                                    });
                                    panelsList = Array.from(panelMap.values());
                                  }
                                  return panelsList;
                                })()}
                                inputPanels={[]}
                              />
                            </Box>
                          </Box>
                        </Box>
                        
                        {/* Right: Statistics & Cutting Sequence */}
                        <Box sx={{ width: { xs: '100%', md: '263px' }, flexShrink: 0, alignSelf: { md: 'flex-start' }, display: 'flex', flexDirection: 'column', bgcolor: themeMode === 'dark' ? '#161b22' : '#f6f8fa', borderRadius: 1, border: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de', height: { md: 'calc(100vh - 132px)' }, maxHeight: { md: 'calc(100vh - 132px)' }, overflow: 'auto' }}>
                          {/* Sheet Statistics */}
                          <Accordion defaultExpanded sx={accordionStyles.root}>
                            <AccordionSummary expandIcon={<ArrowDropDown sx={accordionStyles.expandIcon} />} sx={accordionStyles.summary}>
                              <Typography variant="h6" sx={accordionStyles.title}>Sheet {index + 1} Statistics</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                              <TableContainer sx={{ backgroundColor: themeMode === 'dark' ? '#0d1117' : '#ffffff', border: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de', borderRadius: 1 }}>
                                <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '8px', fontSize: '0.656rem', borderColor: themeMode === 'dark' ? '#30363d' : '#d0d7de', color: themeMode === 'dark' ? '#ffffff' : '#24292f' } }}>
                                  <TableBody>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa', width: '40%' }}>Sheet Size</TableCell><TableCell>{sheetSize.width}{getUnitSymbol(unitSystem)} × {sheetSize.length}{getUnitSymbol(unitSystem)}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Material Type</TableCell><TableCell>{statistics?.materialType || stockSheet.materialType || '-'}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Quantity</TableCell><TableCell>{statistics?.quantity || 1}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Used Area</TableCell><TableCell>{usedPercentage}%</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Wasted Area</TableCell><TableCell>{wastedPercentage}%</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Panels</TableCell><TableCell>{panelsArray.length}</TableCell></TableRow>
                                    <TableRow><TableCell sx={{ fontWeight: 'bold', backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa' }}>Waste Panels</TableCell><TableCell>{numWastePanels}</TableCell></TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </AccordionDetails>
                          </Accordion>

                          {/* Cutting Sequence */}
                          <Accordion defaultExpanded sx={{ ...accordionStyles.root, borderBottom: 'none' }}>
                            <AccordionSummary expandIcon={<ArrowDropDown sx={accordionStyles.expandIcon} />} sx={accordionStyles.summary}>
                              <Typography variant="h6" sx={accordionStyles.title}>Sheet {index + 1} Cutting Sequence</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                              <Box sx={{ border: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de', borderRadius: 1, backgroundColor: themeMode === 'dark' ? '#0d1117' : '#ffffff' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.656rem', color: themeMode === 'dark' ? '#ffffff' : '#24292f' }}>
                                  <thead>
                                    <tr style={{ backgroundColor: themeMode === 'dark' ? '#161b22' : '#f6f8fa', borderBottom: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de', position: 'sticky', top: 0 }}>
                                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>#</th>
                                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>Cut</th>
                                      <th style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>Result</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {cuttingSequence.map((cut, idx) => (
                                      <tr key={idx} style={{ borderBottom: themeMode === 'dark' ? '1px solid #30363d' : '1px solid #d0d7de', backgroundColor: hoveredCuts[index] === idx ? 'rgba(0, 217, 255, 0.2)' : 'transparent', cursor: 'pointer', transition: 'background-color 0.15s' }}
                                        onMouseEnter={() => setHoveredCuts(prev => ({ ...prev, [index]: idx }))}
                                        onMouseLeave={() => setHoveredCuts(prev => { const newState = { ...prev }; delete newState[index]; return newState; })}
                                      >
                                        <td style={{ padding: '8px' }}>{idx + 1}</td>
                                        <td style={{ padding: '8px', fontWeight: (cut.cut_type ?? 'horizontal') === 'horizontal' ? 600 : 400 }}>
                                          {(cut.cut_type ?? 'horizontal') === 'horizontal' ? '→' : '↓'} {formatDimension(cut.relative_position ?? cut.absolute_position ?? 0)}{getUnitSymbol(unitSystem)}
                                        </td>
                                        <td style={{ padding: '8px', fontSize: '0.8rem', color: themeMode === 'dark' ? '#8b949e' : '#57606a' }}>
                                          {(() => {
                                            if (cut.results && Array.isArray(cut.results)) {
                                              const panelResult = cut.results.find(r => r.type === 'panel');
                                              if (panelResult && panelResult.id !== undefined && panelResult.id !== null) return getLabelForPanelId(panelResult.id);
                                            }
                                            if (cut.creates && cut.creates.includes('Finished panel')) {
                                              const match = cut.creates.match(/Finished panel '([^']+)'/);
                                              return match ? match[1] : '';
                                            }
                                            return '';
                                          })()}
                                        </td>
                                      </tr>
                                    ))}
                                    {cuttingSequence.length === 0 && (
                                      <tr><td colSpan="3" style={{ padding: '12px', textAlign: 'center', color: themeMode === 'dark' ? '#8b949e' : '#57606a' }}>No cuts needed</td></tr>
                                    )}
                                  </tbody>
                                </table>
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      </Box>
                    );
                  })
                }
              </Box>

            </>
          )}
        </Box>

      </Box>
    </Box>
    </ThemeProvider>
  );
}

export default App;
